import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response } from '@ui/src';
import {
  TransactionMonitoringRulesValidators,
  TransactionMonitoringRule,
} from '../../../types';
import { transactionMonitoringRulesApi } from '../services';
import ROUTES from '../../../../routes';

export interface TransationMonitoringRulesState {
  requestInProgress: boolean;
  values?: TransactionMonitoringRule[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  selectedRules: string[];
  validators?: TransactionMonitoringRulesValidators;
  pagination?: Pagination;
}

const initialState: TransationMonitoringRulesState = {
  requestInProgress: true,
  values: undefined,
  selectedRules: [],
};

export const transationMonitoringRulesSlice = createSlice({
  name: 'transationMonitoringRules',
  initialState,
  reducers: {
    selectRule: (state, action: PayloadAction<string[]>) => {
      state.selectedRules = action.payload;
    },
    clearTransationMonitoringRulesState: (state) => {
      state.requestInProgress = false;
      state.values = undefined;
      state.selectedRules = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        transactionMonitoringRulesApi.endpoints
          .getTransactionMonitoringRulesValidators.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: TransactionMonitoringRulesValidators }>,
        ) => {
          const {
            createMonitoringRuleSchema,
            updateMonitoringRuleSchema,
            toggleActiveSchema,
          } = action.payload.data;

          state.validators = {
            toggleActiveSchema,
            createMonitoringRuleSchema: {
              ...createMonitoringRuleSchema,
              properties: {
                ...createMonitoringRuleSchema.properties,
                number: {
                  minimum: createMonitoringRuleSchema.properties.number.minimum,
                  type: createMonitoringRuleSchema.properties.number.type,
                },
                periodOfTimeInHours: {
                  minimum:
                    createMonitoringRuleSchema.properties.periodOfTimeInHours
                      .minimum,
                  type: createMonitoringRuleSchema.properties
                    .periodOfTimeInHours.type,
                },
                volumeFrom: {
                  minimum:
                    createMonitoringRuleSchema.properties.volumeFrom.minimum,
                  type: createMonitoringRuleSchema.properties.volumeFrom.type,
                },
                volumeTo: {
                  minimum:
                    createMonitoringRuleSchema.properties.volumeTo.minimum,
                  type: createMonitoringRuleSchema.properties.volumeTo.type,
                },
              },
            },
            updateMonitoringRuleSchema: {
              ...updateMonitoringRuleSchema,
              properties: {
                ...updateMonitoringRuleSchema.properties,
                number: {
                  minimum: updateMonitoringRuleSchema.properties.number.minimum,
                  type: updateMonitoringRuleSchema.properties.number.type,
                },
                periodOfTimeInHours: {
                  minimum:
                    updateMonitoringRuleSchema.properties.periodOfTimeInHours
                      .minimum,
                  type: updateMonitoringRuleSchema.properties
                    .periodOfTimeInHours.type,
                },
                volumeFrom: {
                  minimum:
                    updateMonitoringRuleSchema.properties.volumeFrom.minimum,
                  type: updateMonitoringRuleSchema.properties.volumeFrom.type,
                },
                volumeTo: {
                  minimum:
                    updateMonitoringRuleSchema.properties.volumeTo.minimum,
                  type: updateMonitoringRuleSchema.properties.volumeTo.type,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.getTransactionMonitoringRules
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionMonitoringRule>>) => {
          state.values = action.payload.data.edges.map((item) => ({
            ...item.node,
            url: generatePath(ROUTES.TRANSACTION_MONITORING_RULES_EDIT, {
              ruleId: item.node.id,
            }),
          }));
          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.createTransactionMonitoringRule
          .matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionMonitoringRule }>) => {
          const nextTransactionMonitorinRules = (
            [] as TransactionMonitoringRule[]
          ).concat(state.values as TransactionMonitoringRule[]);

          nextTransactionMonitorinRules.unshift({
            ...action.payload.data,
            url: generatePath(ROUTES.TRANSACTION_MONITORING_RULES_EDIT, {
              ruleId: action.payload.data.id,
            }),
          });

          state.values = nextTransactionMonitorinRules;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.updateTransactionMonitoringRule
          .matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionMonitoringRule }>) => {
          const nextTransactionMonitorinRules = state.values?.map((item) => {
            let result = { ...item };

            if (item.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(ROUTES.TRANSACTION_MONITORING_RULES_EDIT, {
                  ruleId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.values = nextTransactionMonitorinRules;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints
          .toggleTransationMonitoringRulesActive.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionMonitoringRule }>) => {
          if (state.values) {
            const nextTransactionMonitorinRules = state.values?.map((item) => {
              const result = { ...item };

              if (item.id === action.payload.data.id) {
                result.active = action.payload.data.active;
              }

              return result;
            });

            state.values = nextTransactionMonitorinRules;
          }
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints
          .batchToggleTransationMonitoringRulesActive.matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  ids: string[];
                  active: boolean;
                };
              };
            };
          },
        ) => {
          if (state.values) {
            const { ids, active } = action.meta.arg.originalArgs;

            const nextTransactionMonitorinRules = state.values?.map((item) => {
              const result = { ...item };

              const currentRule = ids.find((id) => result.id === id);

              if (item.id === currentRule) {
                result.active = active;
              }

              return result;
            });

            state.values = nextTransactionMonitorinRules;
          }
        },
      );
  },
});

export const { selectRule, clearTransationMonitoringRulesState } =
  transationMonitoringRulesSlice.actions;

export default transationMonitoringRulesSlice.reducer;
